import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Receipt, FileText, CheckCircle, Clock, Euro,
  Download, Eye, Search, Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const ClientReceiptsPage = () => {
  const { user, isAgency } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchReceipts();
  }, []);

  const fetchReceipts = async () => {
    try {
      const response = await api.get('/client/receipts');
      setReceipts(response.data);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      // Demo data
      setReceipts([
        {
          id: 'rcpt-001',
          payment_date: '2024-12-20',
          amount: 2400,
          project_name: 'Refonte Site Web HBS',
          payment_method: 'Virement bancaire',
          reference: 'PAY-2024-001',
          invoice_pdf: null,
          status: 'pending_invoice'
        },
        {
          id: 'rcpt-002',
          payment_date: '2024-12-15',
          amount: 1800,
          project_name: 'Application Mobile HBS Connect',
          payment_method: 'Carte bancaire',
          reference: 'PAY-2024-002',
          invoice_pdf: '/uploads/invoices/invoice-002.pdf',
          status: 'invoice_attached'
        },
        {
          id: 'rcpt-003',
          payment_date: '2024-11-28',
          amount: 3600,
          project_name: 'Campagne Social Media',
          payment_method: 'Virement bancaire',
          reference: 'PAY-2024-003',
          invoice_pdf: '/uploads/invoices/invoice-003.pdf',
          status: 'invoice_attached'
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredReceipts = receipts.filter(receipt =>
    receipt.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    receipt.reference?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: receipts.length,
    pending: receipts.filter(r => r.status === 'pending_invoice').length,
    attached: receipts.filter(r => r.status === 'invoice_attached').length,
    totalAmount: receipts.reduce((sum, r) => sum + r.amount, 0)
  };

  const handleDownload = (receipt) => {
    if (receipt.invoice_pdf) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      window.open(`${backendUrl}${receipt.invoice_pdf}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Mes Factures
            </h1>
            <p className="text-[#64748b] mt-1">
              Consultez et téléchargez vos factures
            </p>
          </div>
        </div>

        {/* Stats - Compact */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Receipt className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>{stats.total}</p>
                <p className="text-xs text-[#64748b]">Total</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>{stats.pending}</p>
                <p className="text-xs text-[#64748b]">En attente</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>{stats.attached}</p>
                <p className="text-xs text-[#64748b]">Disponibles</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <Euro className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>
                  {stats.totalAmount.toLocaleString('fr-FR')}€
                </p>
                <p className="text-xs text-[#64748b]">Total facturé</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="card mb-4">
          <CardContent className="p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
              <Input 
                placeholder="Rechercher par projet ou référence..." 
                className="pl-10 h-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.colors?.primary }} />
              </div>
            ) : filteredReceipts.length === 0 ? (
              <div className="text-center py-12 text-[#64748b]">
                <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Aucune facture trouvée</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#f8fafc]">
                    <TableHead className="font-medium text-xs">Référence</TableHead>
                    <TableHead className="font-medium text-xs">Projet</TableHead>
                    <TableHead className="font-medium text-xs">Date</TableHead>
                    <TableHead className="font-medium text-xs">Méthode</TableHead>
                    <TableHead className="font-medium text-xs text-right">Montant</TableHead>
                    <TableHead className="font-medium text-xs">Statut</TableHead>
                    <TableHead className="font-medium text-xs text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReceipts.map((receipt) => (
                    <TableRow key={receipt.id} className="hover:bg-[#f8fafc]/50">
                      <TableCell className="font-mono text-xs text-[#64748b]">
                        {receipt.reference}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-sm" style={{ color: theme.colors?.primary }}>
                          {receipt.project_name}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-[#64748b]">
                        {new Date(receipt.payment_date).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell className="text-sm text-[#64748b]">
                        {receipt.payment_method}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-semibold" style={{ color: theme.colors?.primary }}>
                          {receipt.amount.toLocaleString('fr-FR')}€
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(
                          "text-[10px]",
                          receipt.status === 'invoice_attached' 
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        )}>
                          {receipt.status === 'invoice_attached' ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Disponible</>
                          ) : (
                            <><Clock className="w-3 h-3 mr-1" /> En attente</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {receipt.invoice_pdf ? (
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-7 w-7 rounded-full"
                              onClick={() => handleDownload(receipt)}
                              title="Voir la facture"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-7 w-7 rounded-full"
                              onClick={() => handleDownload(receipt)}
                              title="Télécharger"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-[#94a3b8] italic">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Info notice for clients */}
        {!isAgency && (
          <p className="text-xs text-[#94a3b8] mt-4 text-center">
            Les factures sont mises à disposition par l'agence après chaque paiement.
          </p>
        )}
      </main>
    </div>
  );
};

export default ClientReceiptsPage;
